import React, { useState, useEffect, Suspense, useRef } from "react";
import { useNavigate } from "react-router-dom";
import SubTitle from "../common/SubTitle";
import Cards from "../common/Cards";
import SearchFilter from "../common/SearchFilter";
import Pagination from "../common/Pagination";
import axios from "axios";
import config from "../../config.js";
import Update_Icon from "../../images/table/Update_icon.svg";
import Delete_Icon from "../../images/table/Delete_icon.svg";
import { usePermission } from "../../hooks/usePermission";
import { CircularProgress } from "@mui/material";
import sortByParam from "../../hooks/SortByParam";
import searchInTheTable from "../../hooks/SarchInTable";
import filterBy from "../../hooks/FilterBy";
import { CARD_BACKGROUND_COLORS, TEXT_COLORS } from "../../constants/styles";
import { ACTIONS_COLUMN_ID } from "../../constants/dashboardConstants";
import DeleteModal from "../common/DeleteModal";
import { useTranslation } from "react-i18next";
import { useUser } from "../../pages/login/UserContextPersistent";
const DynamicTable = React.lazy(() => import("../common/DynamicTable"));

// const headers = ['Site ID', 'Site', 'Description', 'Vehicles', 'Actions'];
const headers = [
  { key: "Site ID", value: "Site ID" },
  { key: "Site", value: "Sites" },
  { key: "Description", value: "Description" },
  { key: "Vehicles", value: "Vehicles" },
  { key: "Site Incharge", value: "Site Incharges" },
  { key: ACTIONS_COLUMN_ID, value: "Actions" },
];

const customActions = [
  {
    id: "update",
    label: "Update",
    icon: Update_Icon,
    path: "/site",
  },
  {
    id: "delete",
    label: "Delete",
    className: "delete",
    icon: Delete_Icon,
    path: "/site/",
  },
];

export default function SitesMain() {
  const { t } = useTranslation();
  const [siteList, setSiteList] = useState([]);
  const [sortedTableData, setSortedTableData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({
    siteId: "",
    name: "",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [siteIdToDelete, setSiteIdToDelete] = useState(null);
  const [sortParam, setSortParam] = useState("siteId");
  const [sortDirection, setSortDirection] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const { dispatch } = useUser();

  const userCacheRef = useRef({});

  // Define filter configuration
  const [filterConfig, setFilterConfig] = useState([
    {
      id: "siteId",
      label: "Site ID",
      helperText: "Select Site ID",
      options: [],
    },
    {
      id: "name",
      label: "Site Name",
      helperText: "Select Site Name",
      options: [],
    },
  ]);

  // In your SitesMain component, update the getAllSites function:
  async function getAllSites() {
    try {
      // Get organizationId from userState in localStorage
      let organizationId;
      const userStateString = localStorage.getItem("userState");
      if (userStateString) {
        const userState = JSON.parse(userStateString);
        organizationId = userState.user.organizationId;
      }

      if (!organizationId) {
        throw new Error("Organization ID not found in localStorage");
      }

      const response = await axios.get(
        `${config.baseURL}/site/organization?organizationId=${organizationId}`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.log("Error in fetching site data: ", error);
      throw error;
    }
  }

  // Function to delete site
  async function deleteSite(id) {
    try {
      const response = await axios.delete(`${config.baseURL}/site/`, {
        data: { id },
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      console.log("Error in deleting site data: ", error);
      throw error;
    }
  }

  // function to fetch user details
  async function getUserById(userId) {
    try {
      // Check cache first
      if (userCacheRef.current[userId]) {
        return userCacheRef.current[userId];
      }

      const response = await axios.get(`${config.baseURL}/user/${userId}`, {
        withCredentials: true,
      });

      // Cache the user data
      userCacheRef.current[userId] = response.data;

      return response.data;
    } catch (error) {
      console.log("Error fetching user data: ", error);
      return null;
    }
  }

  async function fetchSiteInchargeNames(sites) {
    const uniqueInchargeIds = [
      ...new Set(sites.map((site) => site.siteIncharge).filter(Boolean)),
    ];

    await Promise.all(uniqueInchargeIds.map((userId) => getUserById(userId)));

    return sites.map((site) => {
      if (!site.siteIncharge) {
        return {
          ...site,
          siteInchargeName: "N/A",
        };
      }

      const user = userCacheRef.current[site.siteIncharge];

      return {
        ...site,
        siteInchargeName: user?.user?.name || "Unknown",
      };
    });
  }

  // Fetch sites data on component mount
  useEffect(() => {
    let isMounted = true;

    const fetchSites = async () => {
      setIsLoading(true);

      try {
        const sites = await getAllSites();
        // Fetch site incharge names
        const sitesWithNames = await fetchSiteInchargeNames(sites);

        if (!isMounted) {
          return;
        }

        setSiteList(sitesWithNames);

        // Extract unique siteIds for filter
        const siteIds = [];
        const siteIdSet = new Set();
        sites.forEach((site) => {
          if (site.siteId && !siteIdSet.has(site.siteId)) {
            siteIdSet.add(site.siteId);
            siteIds.push({
              value: site.siteId,
              label: site.siteId,
            });
          }
        });

        // Extract unique site names for filter
        const siteNames = [];
        const siteNameSet = new Set();
        sites.forEach((site) => {
          if (site.name && !siteNameSet.has(site.name)) {
            siteNameSet.add(site.name);
            siteNames.push({
              value: site.name,
              label: site.name,
            });
          }
        });

        // Update filter config with dynamic options
        setFilterConfig((prev) => {
          const updated = [...prev];
          const siteIdIndex = updated.findIndex((f) => f.id === "siteId");
          const siteNameIndex = updated.findIndex((f) => f.id === "name");

          if (siteIdIndex >= 0) {
            updated[siteIdIndex].options = siteIds;
          }

          if (siteNameIndex >= 0) {
            updated[siteNameIndex].options = siteNames;
          }

          return updated;
        });
        // console.log("sitesWithNames",sitesWithNames)
      } catch (error) {
        if (!isMounted) {
          return;
        }

        if (error.response && error.response.status === 401) {
          console.log("Unauthorized request");
          dispatch({ type: "CLEAR_USER" });
          navigate("/login", { replace: true });
          return;
        }
        console.error("Error fetching site data:", error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchSites();

    return () => {
      isMounted = false;
    };
  }, [dispatch, navigate]);

  // Apply sorting, filtering and searching whenever relevant state changes
  useEffect(() => {
    let filteredData = [...siteList];

    // Apply search
    if (searchQuery) {
      filteredData = searchInTheTable(searchQuery, filteredData);
    }

    // Apply filters
    if (filters.siteId) {
      filteredData = filterBy("siteId", filters.siteId, filteredData);
    }

    if (filters.name) {
      filteredData = filterBy("name", filters.name, filteredData);
    }

    // Apply sorting
    filteredData = sortByParam(sortParam, filteredData, sortDirection);

    setSortedTableData(filteredData);
  }, [searchQuery, filters, sortParam, sortDirection, siteList]);

  // Format data for table display
  const formatDataForTable = (data) => {
    return data.map((site) => {
      const formattedData = {
        "Site ID": site.siteId || "N/A",
        Site: site.name || "N/A",
        "Site Incharge": site.siteInchargeName || "N/A",
        Description: site.description || "N/A",
        Vehicles: site.vehicles
          ? site.vehicles.map((vehicle) => vehicle.name).join(", ")
          : "N/A",
      };
      // Add original ID for action handling
      formattedData.id = site.id;
      formattedData.originalData = site;

      return formattedData;
    });
  };

  // Handle search changes
  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setCurrentPage(1); // Reset to first page when search changes
  };

  // Handle filter changes
  const handleFilterChange = (filterValues) => {
    setFilters(filterValues);
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Handle sort change
  const handleSortChange = (param) => {
    const newSortDirection =
      sortParam === param ? (sortDirection === "asc" ? "desc" : "asc") : "asc";
    setSortDirection(newSortDirection);
    setSortParam(param);
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Calculate pagination indexes
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedTableData.slice(indexOfFirstItem, indexOfLastItem);

  // Add site handler
  const handleAddSite = () => {
    navigate("/site/add");
  };

  // Handle action clicks
  const handleActionClick = (actionId, rowIndex, actionObj) => {
    // Get the actual site data for this row
    const site = currentItems[rowIndex];

    switch (actionId) {
      case "update":
        navigate(`/site/${site.id}`);
        break;
      case "delete":
        setSiteIdToDelete(site.id);
        setDeleteModalOpen(true);
        break;
      default:
        break;
    }
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    try {
      if (siteIdToDelete) {
        await deleteSite(siteIdToDelete);
        setSiteList((prevList) =>
          prevList.filter((site) => site.id !== siteIdToDelete)
        );
        // Show toast notification or success message here
      }
    } catch (error) {
      console.error("Error deleting site:", error);
      // Show error notification here
    } finally {
      setDeleteModalOpen(false);
      setSiteIdToDelete(null);
    }
  };

  // Handle delete cancellation
  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setSiteIdToDelete(null);
  };
  const orgName = JSON.parse(localStorage.getItem("userState"))?.organization
    ?.name;
  return (
    <div style={{ minHeight: "100vh" }}>
      <SubTitle
        title="Sites"
        path={`${orgName || "organization"} > Sites`}
        add="+ Create Sites"
        addPath="/site/add"
        onAddClick={handleAddSite}
      />

      <Cards
        cardData={[
          {
            title: "Total Sites",
            value: siteList?.length || 0,
            backgroundColor: CARD_BACKGROUND_COLORS.DARK_BLUE,
            titleColor: TEXT_COLORS.WHITE,
            valueColor: TEXT_COLORS.WHITE,
          },
        ]}
      />

      <div className="table_container">
        <SearchFilter
          filters={filterConfig}
          searchPlaceholder="Search"
          onSearchChange={handleSearchChange}
          onFilterChange={handleFilterChange}
        />

        <Suspense fallback={<CircularProgress />}>
          {/* {isLoading ? (
                        <div className="loader-container">
                            <CircularProgress />
                        </div>
                    ) : ( */}
          <DynamicTable
            headers={headers}
            // data={formatDataForTable(currentItems || [])}
            data={formatDataForTable(sortedTableData || [])}
            onActionClick={handleActionClick}
            onSortChange={handleSortChange}
            sortParam={sortParam}
            sortDirection={sortDirection}
            actions={
              hasPermission("/api/site", "DELETE")
                ? customActions
                : customActions.filter((action) => action.id !== "delete")
            }
            loading={isLoading}
          />
          {/* )} */}
        </Suspense>

        {/* <Pagination
                    totalItems={sortedTableData?.length || 0}
                    itemsPerPage={itemsPerPage}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                /> */}
      </div>

      {/* Delete Confirmation Dialog */}

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Site!"
        message={
          <>
            {t("Are you sure you want to delete")}{" "}
            <span style={{ fontWeight: 600, color: "#d32f2f" }}>
              {siteIdToDelete?.name || t("this site")}
            </span>
            ? {t("This action cannot be undone.")}
          </>
        }
      />
    </div>
  );
}
